/**
 * SecureEmbed Provenance Explorer Page
 *
 * Tier 1a - Provenance chain explorer and aggregate analysis
 */

import React, { useState, useCallback } from 'react';
import {
  GitBranch, Search, Loader2, AlertCircle, RefreshCw
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { provenanceApi, aggregateApi, ProvenanceRecord } from '../services/api';
import { CHART_COLORS, RECHARTS_THEME } from '../constants/colors';
import ProvenanceChain from '../components/ProvenanceChain';
import ChartCard from '../components/ChartCard';
import TierGate from '../components/TierGate';
import InfoTooltip from '../components/InfoTooltip';
import RelatedConnections from '../components/RelatedConnections';

const Provenance: React.FC = () => {
  const [embeddingId, setEmbeddingId] = useState('');
  const [chain, setChain] = useState<ProvenanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Aggregate
  const [aggType, setAggType] = useState('count_by_source');
  const [aggData, setAggData] = useState<any[]>([]);
  const [aggLoading, setAggLoading] = useState(false);

  const fetchChain = useCallback(async () => {
    if (!embeddingId.trim()) return;
    setLoading(true);
    setError(null);
    setChain([]);

    try {
      const response = await provenanceApi.getChain(embeddingId.trim());
      if (response.success && response.data) {
        setChain(response.data.chain || []);
      } else {
        throw new Error('No provenance data found');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch provenance chain');
    } finally {
      setLoading(false);
    }
  }, [embeddingId]);

  const fetchAggregate = useCallback(async () => {
    setAggLoading(true);
    try {
      const response = await aggregateApi.run({ type: aggType });
      if (response?.success && response?.data?.results) {
        setAggData(response.data.results.map(r => ({
          name: r.label,
          value: r.value,
        })));
      }
    } catch (err: any) {
      console.warn('Aggregate fetch failed:', err.message);
    } finally {
      setAggLoading(false);
    }
  }, [aggType]);

  return (
    <TierGate requiredTier={1.5}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <GitBranch className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Provenance Explorer</h1>
            <p className="text-gray-500 text-sm">Trace the lineage of embeddings and insights</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 mb-2">Embedding ID</label>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={embeddingId}
                onChange={(e) => setEmbeddingId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchChain()}
                placeholder="Enter an embedding ID to trace its provenance..."
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2B064F]/50 focus:border-[#2B064F] font-mono text-sm"
              />
            </div>
            <button
              onClick={fetchChain}
              disabled={loading || !embeddingId.trim()}
              className="px-6 py-3 bg-[#2B064F] text-white rounded-xl font-medium hover:bg-[#3D0A6B] transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Trace'}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Chain */}
        {chain.length > 0 && (
          <>
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-1.5">Provenance Chain <InfoTooltip tooltipId="provenance.chain" /></h2>
              <ProvenanceChain chain={chain} />
            </div>

            {/* Related connections for this embedding */}
            <RelatedConnections
              query={embeddingId.trim()}
              maxPerGroup={3}
              maxGroups={3}
              startCollapsed={false}
              label="Related Connections"
            />
          </>
        )}

        {!loading && chain.length === 0 && !error && (
          <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
            <GitBranch className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-700 font-medium">Enter an embedding ID to trace its provenance</p>
            <p className="text-gray-500 text-sm mt-1">View the complete lineage from ingestion to insight generation</p>
          </div>
        )}

        {/* Aggregate Analysis */}
        <ChartCard
          title="Aggregate Analysis"
          subtitle="Cross-datasource statistics"
          tooltipId="provenance.aggregate"
          action={
            <div className="flex items-center gap-2">
              <select
                value={aggType}
                onChange={(e) => setAggType(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B064F]/50"
              >
                <option value="count_by_source">Count by Source</option>
                <option value="count_by_type">Count by Type</option>
                <option value="avg_confidence">Avg Confidence</option>
              </select>
              <button
                onClick={fetchAggregate}
                disabled={aggLoading}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${aggLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          }
        >
          {aggData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={aggData}>
                <CartesianGrid {...RECHARTS_THEME.grid} />
                <XAxis dataKey="name" tick={RECHARTS_THEME.label} />
                <YAxis tick={RECHARTS_THEME.label} />
                <Tooltip contentStyle={RECHARTS_THEME.tooltip.contentStyle} />
                <Bar dataKey="value" fill={CHART_COLORS.primary} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              <p>Click refresh to load aggregate data</p>
            </div>
          )}
        </ChartCard>
      </div>
    </TierGate>
  );
};

export default Provenance;
